import axios from 'axios'

/** Lazy-loaded so a parse error in echo/pusher deps does not break the whole app. */
async function loadEchoLib() {
  if (loadEchoLib._cache) {
    return loadEchoLib._cache
  }
  try {
    const mod = await import('@/libs/echo')
    loadEchoLib._cache = mod.default || mod
  } catch (e) {
    console.warn('[RealtimeNotifications] Echo module failed to load; using polling only.', e)
    loadEchoLib._cache = {
      isEchoConfigured: () => false,
      initEcho: () => null,
      disconnectEcho: () => {},
      userChannel: (id) => `App.Models.User.${id}`,
    }
  }
  return loadEchoLib._cache
}

const EVENT_NAME = 'user.notification'
const BUS_EVENT = 'realtime-notification'
const POLL_INTERVAL_NO_PUSHER_MS = 10000
const POLL_INTERVAL_WITH_PUSHER_MS = 60000
const DEDUPE_TTL_MS = 120000

class RealtimeNotificationService {
  constructor() {
    this.userId = null
    this.router = null
    this.eventBus = null
    this.echoChannel = null
    this.permissionGranted = false
    this.initialized = false
    this.pusherConnected = false
    this.pollTimer = null
    this.pollSeeded = false
    this.knownNotificationIds = new Set()
    this.recentlyHandledIds = new Map()
  }

  async requestPermission() {
    if (typeof window === 'undefined' || !('Notification' in window)) {
      return 'unsupported'
    }

    if (Notification.permission === 'granted') {
      this.permissionGranted = true
      return 'granted'
    }

    if (Notification.permission === 'denied') {
      return 'denied'
    }

    try {
      const result = await Notification.requestPermission()
      this.permissionGranted = result === 'granted'
      return result
    } catch (e) {
      return 'denied'
    }
  }

  async init(userId, router, eventBus) {
    if (!userId) {
      return false
    }

    this.destroy()

    this.userId = userId
    this.router = router
    this.eventBus = eventBus

    await this.requestPermission()
    await this.logBackendConfig()

    const echoLib = await loadEchoLib()
    if (echoLib.isEchoConfigured()) {
      this.pusherConnected = await this.initPusher(userId)
    } else {
      console.warn(
        '[RealtimeNotifications] VUE_APP_PUSHER_APP_KEY is empty — using polling fallback. '
        + 'Add your Pusher key to .env and restart npm run serve for instant websocket delivery.'
      )
    }

    this.startPolling()

    this.initialized = true
    return true
  }

  async logBackendConfig() {
    const baseApi = process.env.VUE_APP_APIENDPOINT
    const token = localStorage.getItem('accessToken')
    if (!baseApi || !token) {
      return
    }

    try {
      const { data } = await axios.get(`${baseApi}/notifications/realtime-config`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      if (data && !data.pusher_ready) {
        console.warn('[RealtimeNotifications] API broadcasting:', data.message || data.broadcast_driver)
      } else if (data && data.pusher_ready) {
        console.info('[RealtimeNotifications] API Pusher broadcasting is enabled')
      }
    } catch (e) {
      // endpoint may be unavailable on older deployments
    }
  }

  async initPusher(userId) {
    const { initEcho, userChannel } = await loadEchoLib()
    const echo = initEcho(userId)
    if (!echo) {
      console.warn('[RealtimeNotifications] Echo init failed — check accessToken')
      return false
    }

    const channelName = userChannel(userId)
    this.echoChannel = echo.private(channelName)

    this.echoChannel.listen(`.${EVENT_NAME}`, (payload) => {
      this.handlePayload(payload, 'pusher')
    })

    if (typeof this.echoChannel.error === 'function') {
      this.echoChannel.error((error) => {
        console.error('[RealtimeNotifications] Private channel error:', error)
      })
    }

    const pusher = echo.connector && echo.connector.pusher
    if (pusher) {
      pusher.connection.bind('connected', () => {
        console.info('[RealtimeNotifications] Pusher connected on', channelName)
      })
      pusher.connection.bind('error', (err) => {
        console.error('[RealtimeNotifications] Pusher connection error:', err)
      })
    }

    return true
  }

  startPolling() {
    this.stopPolling()
    this.pollSeeded = false
    this.knownNotificationIds = new Set()

    const interval = this.pusherConnected
      ? POLL_INTERVAL_WITH_PUSHER_MS
      : POLL_INTERVAL_NO_PUSHER_MS

    const poll = async () => {
      await this.pollUnreadNotifications()
    }

    poll()
    this.pollTimer = setInterval(poll, interval)
  }

  stopPolling() {
    if (this.pollTimer) {
      clearInterval(this.pollTimer)
      this.pollTimer = null
    }
  }

  async pollUnreadNotifications() {
    const list = await this.fetchUnreadNotifications()
    if (!Array.isArray(list)) {
      return
    }

    const currentIds = new Set(list.map((n) => n.id))

    if (!this.pollSeeded) {
      currentIds.forEach((id) => this.knownNotificationIds.add(id))
      this.pollSeeded = true
      return
    }

    list.forEach((notification) => {
      if (!this.knownNotificationIds.has(notification.id)) {
        this.knownNotificationIds.add(notification.id)
        this.handlePayload(this.normalizeNotification(notification), 'poll')
      }
    })

  }

  normalizeNotification(row) {
    let extraData = row.extra_data
    if (typeof extraData === 'string') {
      try {
        extraData = JSON.parse(extraData)
      } catch (e) {
        extraData = {}
      }
    }

    return {
      id: row.id,
      title: row.title,
      message: row.text || row.message,
      text: row.text,
      type: row.type,
      user_id: row.user_id,
      extra_data: extraData || {},
    }
  }

  async fetchUnreadNotifications() {
    const token = localStorage.getItem('accessToken')
    const baseApi = process.env.VUE_APP_APIENDPOINT
    if (!token || !baseApi) {
      return []
    }

    try {
      const { data } = await axios.get(`${baseApi}/send-notification`, {
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: 'application/json',
        },
      })
      return Array.isArray(data) ? data : []
    } catch (e) {
      return []
    }
  }

  teardownChannel() {
    if (this.echoChannel) {
      try {
        this.echoChannel.stopListening(`.${EVENT_NAME}`)
      } catch (e) {
        // channel may already be gone
      }
      this.echoChannel = null
    }
  }

  destroy() {
    this.stopPolling()
    this.teardownChannel()
    loadEchoLib().then((echoLib) => {
      echoLib.disconnectEcho()
    }).catch(() => {})
    this.userId = null
    this.router = null
    this.eventBus = null
    this.initialized = false
    this.pusherConnected = false
    this.pollSeeded = false
    this.knownNotificationIds = new Set()
    this.recentlyHandledIds = new Map()
  }

  shouldSkipDuplicate(id) {
    if (!id) {
      return false
    }
    if (this.recentlyHandledIds.has(id)) {
      return true
    }
    this.recentlyHandledIds.set(id, Date.now())
    const now = Date.now()
    this.recentlyHandledIds.forEach((ts, key) => {
      if (now - ts > DEDUPE_TTL_MS) {
        this.recentlyHandledIds.delete(key)
      }
    })
    return false
  }

  handlePayload(payload, source = 'unknown') {
    if (!payload) {
      return
    }

    const id = payload.id
    if (this.shouldSkipDuplicate(id)) {
      return
    }

    const title = payload.title || 'Notification'
    const message = payload.message || payload.text || ''
    const extraData = payload.extra_data || {}
    const url = extraData.url || '/'

    if (process.env.NODE_ENV !== 'production') {
      console.info('[RealtimeNotifications] New notification via', source, { id, title })
    }

    this.showBrowserNotification({
      id,
      title,
      message,
      url,
    })

    if (this.eventBus) {
      this.eventBus.$emit(BUS_EVENT, payload)
    }
  }

  showBrowserNotification({ id, title, message, url, icon }) {
    if (typeof window === 'undefined' || !('Notification' in window)) {
      return
    }

    if (Notification.permission === 'default') {
      this.requestPermission()
      return
    }

    if (Notification.permission !== 'granted') {
      return
    }

    try {
      const notification = new Notification(title, {
        body: message,
        icon: icon || '/favicon.ico',
        tag: id ? `notification-${id}` : `notification-${Date.now()}`,
        data: { url },
        requireInteraction: false,
      })

      notification.onclick = (event) => {
        event.preventDefault()
        window.focus()
        this.onNotificationClick(url)
        notification.close()
      }
    } catch (e) {
      console.warn('[RealtimeNotifications] Browser notification failed:', e)
    }
  }

  onNotificationClick(url) {
    if (!url || !this.router) {
      return
    }

    const path = url.startsWith('/') ? url : `/${url}`
    if (this.router.currentRoute && this.router.currentRoute.path === path) {
      return
    }
    this.router.push(path).catch(() => {})
  }

  async isConfigured() {
    const echoLib = await loadEchoLib()
    return echoLib.isEchoConfigured()
  }

  isInitialized() {
    return this.initialized
  }

  usesPollingFallback() {
    return this.initialized && !this.pusherConnected
  }
}

export const realtimeNotificationService = new RealtimeNotificationService()
export const REALTIME_NOTIFICATION_BUS_EVENT = BUS_EVENT
